"use client";

import ProductRecommendation from "@/app/ec/product/recommendations/productRecommendation";
import { RecommendationsCarouselSlideBreakpoints } from "@/utils/getRecommendationsCarouselSlidesPerView";
import CaretLeftIcon from "public/next-public/common/icons/caret-solid-left.svg";
import CaretRightIcon from "public/next-public/common/icons/caret-solid-right.svg";
import React, { FunctionComponent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import type { Swiper as SwiperInstance } from "swiper";
import { Grid, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import style from "./recommendationsSlider.module.scss";

const RecommendationsSlider: FunctionComponent<RecommendationsSlidersProps> = ({
  children,
  className = "",
  displayGrid,
  fullWidthGrid = false,
  showLoadingRecommendations,
  rows = 3,
  compactMobilePagination = false,
  slideBreakpoints,
  desktopPlaceholderCount = 0,
  renderDesktopPlaceholder,
}) => {
  const navigationPrevRef = useRef<HTMLDivElement>(null);
  const navigationNextRef = useRef<HTMLDivElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);
  const [desktopSwiper, setDesktopSwiper] = useState<SwiperInstance | null>(null);
  const [mobileSwiper, setMobileSwiper] = useState<SwiperInstance | null>(null);

  // The arrows and the bullet strip live outside the Swiper's own render, so their refs are still
  // null while Swiper initialises: it would come up with no navigation or pagination attached and
  // clicking an arrow would do nothing. Wire them up once every ref is populated, then re-init.
  useEffect(() => {
    if (!desktopSwiper || desktopSwiper.destroyed) return;
    const navigation = desktopSwiper.params.navigation;
    if (typeof navigation === "boolean" || !navigation) return;
    navigation.prevEl = navigationPrevRef.current;
    navigation.nextEl = navigationNextRef.current;
    desktopSwiper.navigation.destroy();
    desktopSwiper.navigation.init();
    desktopSwiper.navigation.update();
  }, [desktopSwiper]);

  useEffect(() => {
    if (!mobileSwiper || mobileSwiper.destroyed) return;
    const pagination = mobileSwiper.params.pagination;
    if (typeof pagination === "boolean" || !pagination) return;
    pagination.el = paginationRef.current;
    mobileSwiper.pagination.destroy();
    mobileSwiper.pagination.init();
    mobileSwiper.pagination.render();
    mobileSwiper.pagination.update();
  }, [mobileSwiper]);

  const loadingRecommendations = useMemo(() => {
    const max = 6;
    const loadingRecommendations = [];
    for (let index = 0; index < max; index++) {
      loadingRecommendations.push("");
    }
    return loadingRecommendations;
  }, []);

  const recommendationsDisplay = (
    <>
      {showLoadingRecommendations &&
        loadingRecommendations.map((value, index) => {
          return (
            <SwiperSlide key={`loading-slide-${index}`}>
              <ProductRecommendation key={index} isLoadingRecommendation={true} />
            </SwiperSlide>
          );
        })}
    </>
  );

  const childrenDisplay = (
    <>
      {React.Children.map(children, (child, index) => {
        if (React.isValidElement(child)) {
          return <SwiperSlide key={child.key ? child.key : index}>{child}</SwiperSlide>;
        }
      })}
    </>
  );

  return (
    <>
      <div className={`d-none d-md-flex align-items-center gap-3 ${className}`}>
        {displayGrid ? (
          <Swiper
            grid={{
              rows: 2,
              fill: "row",
            }}
            modules={[Grid]}
            navigation={false}
            className={`flex-grow-1 ${style.recommendationsSlider} ${style.maxGridWith} ${fullWidthGrid ? style.fullWidthGrid : ""}`.trim()}
            breakpoints={{
              0: {
                slidesPerView: slideBreakpoints?.[0] || 1,
              },
              250: {
                slidesPerView: slideBreakpoints?.[250] || 1,
              },
              500: {
                slidesPerView: slideBreakpoints?.[500] || 1,
              },
              750: {
                slidesPerView: slideBreakpoints?.[750] || 2,
              },
              1000: {
                slidesPerView: slideBreakpoints?.[1000] || 3,
              },
              1250: {
                slidesPerView: slideBreakpoints?.[1250] || 4,
              },
            }}
          >
            {recommendationsDisplay}
            {childrenDisplay}
          </Swiper>
        ) : (
          <>
            <div ref={navigationPrevRef}>
              <CaretLeftIcon className={style.swiperArrow} height={24} width={24} color="currentColor" />
            </div>
            <Swiper
              className={`flex-grow-1 ${style.recommendationsSlider}`}
              modules={[Navigation]}
              navigation={{
                prevEl: navigationPrevRef.current,
                nextEl: navigationNextRef.current,
                lockClass: style.locked,
                disabledClass: style.disabled,
              }}
              onSwiper={setDesktopSwiper}
              breakpoints={{
                0: {
                  slidesPerView: slideBreakpoints?.[0] || 1,
                  slidesPerGroup: slideBreakpoints?.[0] || 1,
                },
                250: {
                  slidesPerView: slideBreakpoints?.[250] || 2,
                  slidesPerGroup: slideBreakpoints?.[250] || 2,
                },
                500: {
                  slidesPerView: slideBreakpoints?.[500] || 3,
                  slidesPerGroup: slideBreakpoints?.[500] || 3,
                },
                750: {
                  slidesPerView: slideBreakpoints?.[750] || 2,
                  slidesPerGroup: slideBreakpoints?.[750] || 2,
                },
                1000: {
                  slidesPerView: slideBreakpoints?.[1000] || 3,
                  slidesPerGroup: slideBreakpoints?.[1000] || 3,
                },
                1250: {
                  slidesPerView: slideBreakpoints?.[1250] || 4,
                  slidesPerGroup: slideBreakpoints?.[1250] || 4,
                },
              }}
              simulateTouch={false}
              direction="horizontal"
            >
              {recommendationsDisplay}
              {childrenDisplay}
              {desktopPlaceholderCount > 0 &&
                renderDesktopPlaceholder &&
                Array.from({ length: desktopPlaceholderCount }, (_, index) => (
                  <SwiperSlide key={`desktop-placeholder-${index}`}>{renderDesktopPlaceholder()}</SwiperSlide>
                ))}
            </Swiper>
            <div ref={navigationNextRef}>
              <CaretRightIcon className={style.swiperArrow} height={24} width={24} color="currentColor" />
            </div>
          </>
        )}
      </div>
      <div className={`d-block d-md-none ${className}`}>
        <Swiper
          className={`${style.recommendationsSlider} ${style.mobile}`}
          autoHeight={true}
          modules={[Pagination, Grid]}
          pagination={{
            el: paginationRef.current,
            type: "bullets",
            clickable: true,
          }}
          onSwiper={setMobileSwiper}
          grid={{
            rows,
            fill: "row",
          }}
          simulateTouch={false}
          direction="horizontal"
        >
          {recommendationsDisplay}
          {childrenDisplay}
          <div
            className={`${style.pagination}${compactMobilePagination ? ` ${style.paginationCompact}` : ""}`}
            ref={paginationRef}
          ></div>
        </Swiper>
      </div>
    </>
  );
};

interface RecommendationsSlidersProps {
  children: ReactNode;
  className?: string;
  showLoadingRecommendations: boolean;
  displayGrid?: boolean;
  /** Make the desktop grid full-width (e.g. homepage grid). */
  fullWidthGrid?: boolean;
  rows?: number;
  slideBreakpoints?: RecommendationsCarouselSlideBreakpoints;
  /** Tighter dots for mobile layouts (e.g. Weekly Specials). */
  compactMobilePagination?: boolean;
  /** Extra slides appended only on the desktop (!displayGrid) carousel (e.g. empty cells). */
  desktopPlaceholderCount?: number;
  renderDesktopPlaceholder?: () => ReactNode;
}

export default RecommendationsSlider;
